import { EColumnType, IColumnData } from '../data/model/model';
import { IPoint2d } from '../math/IPoint2d';
import { LineSeg2d } from '../math/LineSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';

export function getColumnBoxOutline(columnData: IColumnData, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  if (
    columnData.type === EColumnType.squareColumn &&
    columnData.width !== undefined &&
    columnData.thickness !== undefined
  ) {
    const { width, thickness } = columnData;
    outline.push({ x: -width / 2 + point.x, y: -thickness / 2 + point.y });
    outline.push({ x: -width / 2 + point.x, y: thickness / 2 + point.y });
    outline.push({ x: width / 2 + point.x, y: thickness / 2 + point.y });
    outline.push({ x: width / 2 + point.x, y: -thickness / 2 + point.y });
  } else if (columnData.type === EColumnType.cylinder && columnData.radius !== undefined) {
    const { radius } = columnData;
    outline.push({ x: -radius + point.x, y: -radius + point.y });
    outline.push({ x: -radius + point.x, y: radius + point.y });
    outline.push({ x: radius + point.x, y: radius + point.y });
    outline.push({ x: radius + point.x, y: -radius + point.y });
  }

  return outline;
}

export function getColumnBaseOutline(columnData: IColumnData, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  if (
    columnData.type === EColumnType.squareColumn &&
    columnData.width !== undefined &&
    columnData.thickness !== undefined
  ) {
    const { width, thickness } = columnData;
    outline.push({ x: -width / 2 + point.x, y: -thickness / 2 + point.y });
    outline.push({ x: -width / 2 + point.x, y: thickness / 2 + point.y });
    outline.push({ x: width / 2 + point.x, y: thickness / 2 + point.y });
    outline.push({ x: width / 2 + point.x, y: -thickness / 2 + point.y });
  } else if (columnData.type === EColumnType.cylinder && columnData.radius) {
    const { radius } = columnData;
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      outline.push({
        x: Math.cos(angle) * radius + point.x,
        y: Math.sin(angle) * radius + point.y
      });
    }
    outline.reverse();
  }

  return outline;
}

export function getColumnOutline(columnData: IColumnData) {
  const outlineSegs: LineSeg2d[] = [];
  const outline = getColumnBoxOutline(columnData, columnData.center);
  const len = outline.length;
  for (let i = 0; i < len; i++) {
    const seg = new LineSeg2d(
      Point2dUtils.rotatePoint(outline[i], columnData.center, columnData.rotation),
      Point2dUtils.rotatePoint(outline[(i + 1) % len], columnData.center, columnData.rotation)
    );
    outlineSegs.push(seg);
  }

  return outlineSegs;
}

export function getColumnRegionBoxOutline(columnData: IColumnData, point: IPoint2d) {
  const outline: IPoint2d[] = [];
  if (columnData.type === EColumnType.squareColumn) {
    const baseOutline = getColumnBoxOutline(columnData, point);
    const len = baseOutline.length;
    for (let i = 0; i < len; i++) {
      outline.push(Point2dUtils.rotatePoint(baseOutline[i], point, columnData.rotation));
    }
  } else if (columnData.type === EColumnType.cylinder && columnData.radius) {
    const { radius } = columnData;
    outline.push({ x: -radius + point.x, y: -radius + point.y });
    outline.push({ x: -radius + point.x, y: radius + point.y });
    outline.push({ x: radius + point.x, y: radius + point.y });
    outline.push({ x: radius + point.x, y: -radius + point.y });
  }

  return outline;
}

export function getColumnRegionOutline(columnData: IColumnData, point: IPoint2d) {
  const outline: IPoint2d[] = [];
  if (columnData.type === EColumnType.squareColumn) {
    const baseOutline = getColumnBoxOutline(columnData, point);
    const len = baseOutline.length;
    for (let i = 0; i < len; i++) {
      outline.push(Point2dUtils.rotatePoint(baseOutline[i], point, columnData.rotation));
    }
  } else if (columnData.type === EColumnType.cylinder && columnData.radius) {
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      outline.push({
        x: Math.cos(angle) * columnData.radius + point.x,
        y: Math.sin(angle) * columnData.radius + point.y
      });
    }
    outline.reverse();
  }

  return outline;
}
